// Inline "add a task" card for related-list panels.
// Pre-fills contact/account/deal based on where it's embedded.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";

interface Props {
  contactId?: string;
  accountId?: string;
  dealId?: string;
  campaignId?: string;
  /** Query keys to invalidate after the task is created. */
  invalidateQueryKeys?: unknown[][];
}

export function QuickAddTask({ contactId, accountId, dealId, campaignId, invalidateQueryKeys = [] }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState<"LOW" | "NORMAL" | "HIGH">("NORMAL");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      await api.post("/tasks", {
        subject,
        dueDate: dueDate || undefined,
        priority,
        contactId, accountId, dealId, campaignId,
      });
    },
    onSuccess: () => {
      setSubject(""); setDueDate(""); setPriority("NORMAL"); setOpen(false); setError(null);
      invalidateQueryKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Failed to create task."),
  });

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Plus className="mr-1 h-4 w-4"/>Add task
      </Button>
    );
  }

  return (
    <div className="rounded-md border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium">New task</div>
        <button onClick={() => setOpen(false)} aria-label="Close" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4"/>
        </button>
      </div>
      <div className="grid gap-2 md:grid-cols-[1fr_140px_120px_auto]">
        <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        <Select value={priority} onChange={(e) => setPriority(e.target.value as any)}>
          <option value="LOW">Low</option>
          <option value="NORMAL">Normal</option>
          <option value="HIGH">High</option>
        </Select>
        <Button onClick={() => mut.mutate()} disabled={!subject || mut.isPending}>
          {mut.isPending ? "Saving…" : "Add"}
        </Button>
      </div>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
}
